import { query } from "@archipad-js/core/orm";

import { MapEntity } from "@archipad-js/archipad-model";
import { ProjectEntity } from "@archipad-js/archipad-model";
import { RegionEntity } from "@archipad-js/archipad-model";
import { VisitEntity } from "@archipad-js/archipad-model";

import { MAP_FILE_TYPES, MAP_TYPES } from "@archipad-js/archipad-model";
import { comparatorForType, Expression } from "@archipad-js/core/query";

export const MOVABLE_MAP_TYPES = [MAP_TYPES.DEFAULT];

export class MapService {
    public listMaps(options: {
        where: {
            project: ProjectEntity;
            fileTypes?: ReadonlyArray<MAP_FILE_TYPES>;
            regions?: ReadonlyArray<RegionEntity | null>;
            types?: ReadonlyArray<MAP_TYPES>;
        };
        orderBy?: string;
    }): readonly MapEntity[] {
        const defaultOrderBy = "typeOrderHint asc, filename(natural)";
        const { where, orderBy = defaultOrderBy } = options;
        const { project, fileTypes, regions, types } = where;
        const entityContext = project.getContext();

        const formObsType = MAP_TYPES.FORMS;
        const formObsWithBugs = Expression.or(
            Expression.parse("type != {formObsType}", { formObsType }),
            Expression.and(
                Expression.parse("type == {formObsType}", { formObsType }),
                Expression.parse("count(bugs) > 0"),
            ),
        );

        const typesPredicate = types
            ? Expression.and(
                  Expression.parse("type IN {types}", { types }),
                  types.includes(MAP_TYPES.FORMS) ? formObsWithBugs : Expression.true,
              )
            : formObsWithBugs;

        const expression = Expression.and(
            Expression.parse("project == {project}", { project }),
            fileTypes ? Expression.parse("fileType IN {fileTypes}", { fileTypes }) : Expression.true,
            regions ? Expression.parse("region IN {regions}", { regions }) : Expression.true,
            typesPredicate,
        );

        const q = query(entityContext, "Map").predicate(expression).orderBy(orderBy).orderBy("id asc");

        const resultSet = q.execute();
        const maps = resultSet.sortedEntities();
        return maps;
    }

    /**
     * Gives all the ordered plans related to a visit.
     * Unclassified and related to a zone
     *
     * @todo move this method in MapFacade
     */
    public getAllMapsRelatedToAVisit(visit: VisitEntity): readonly MapEntity[] {
        let regions: ReadonlyArray<RegionEntity | null> | undefined;
        if (visit.region !== null) {
            regions = [null, visit.region, ...visit.region.deepChildrenRegions];
        }
        return this.listMaps({ where: { project: visit.project, regions } }).filter((map) => {
            const isFormObs = map.type === MAP_TYPES.FORMS;
            const isNotEmpty = map.bugs.length !== 0;
            return !isFormObs || (isFormObs && isNotEmpty);
        });
    }

    public isMovableMap(map: MapEntity): boolean {
        const isMovableTypes = MOVABLE_MAP_TYPES.includes(map.type);
        return isMovableTypes;
    }

    public compareMaps(mapA: MapEntity, mapB: MapEntity): number {
        if (mapA.typeOrderHint !== mapB.typeOrderHint) {
            return (mapA.typeOrderHint ?? 0) - (mapB.typeOrderHint ?? 0);
        }
        const naturalSort = comparatorForType("string", "natural");
        return naturalSort(mapA.filename, mapB.filename);
    }
}
